import { isNullOrUndefined } from '../../../lib/typeGuards';
import { BlockType } from '../../../types/api/ElementType';
import { Icons } from '../../atoms/SVG';
import { focusNonFocusableElementAndToggleClassName } from '../../../lib/DomUtils/utils';
import {
  ContentWrapperAction,
  ContentWrapperAction as Action,
  ContentWrapperBlocks,
  ContentWrapperBlockTypes,
} from './ContentWrapper.types';

/** Returns the data-index attribute
 *
 * ONLY used in ContentWrapper
 */
export function getIndex(index?: number, parentIndex?: number, grandParentIndex?: number, forHtml?: boolean): string;
export function getIndex(index?: undefined, parentIndex?: number, grandParentIndex?: number, forHtml?: boolean): null;
export function getIndex(
  index?: number,
  parentIndex?: number,
  grandParentIndex?: number,
  forHtml = false
): string | null {
  // ...props.index is only defined inside a ContentWrapper
  if (isNullOrUndefined(index)) {
    // if that prop is null or undefined, we're not inside a ContentWrapper -> early exit
    return null;
  }

  // otherwise we have 3 options:
  // 1. parentIndex is undefined: child element of ContentWrapper
  // 2. parentIndex IS defined -> we are a child element of a LayoutGroup OR CollapsibleBlock
  // 3. parentIndex IS defined AND grandParentIndex is defined
  //    -> we are a child element of a LayoutGroup IN a CollapsibleBlock
  const indexString = [grandParentIndex, parentIndex, index]
    .filter(val => val !== undefined)
    .map(val => val + 1)
    .join('-');

  // dashed on HTML, dotted in UI
  return forHtml ? `block-${indexString}` : indexString.replaceAll('-', '.');
}

export type Position = { position: number | string | null } | LayoutGroupPosition;

type LayoutGroupPosition = { positionImage: string | number; positionText: string | number };
/**
 * All buttons need positions except connect blocks.
 * */
export function getPosition(
  index: number,
  parentIndex: number | undefined,
  elementType: ContentWrapperBlockTypes,
  isNestedInCollapsibleOrLayoutGroup: boolean | undefined,
  actionType: ContentWrapperAction.connectBlocks
): LayoutGroupPosition;
export function getPosition(
  index: number,
  parentIndex: number | undefined,
  elementType: string | undefined,
  isNestedInCollapsibleOrLayoutGroup: boolean | undefined,
  actionType:
    | ContentWrapperAction.addBlock
    | ContentWrapperAction.deleteBlock
    | ContentWrapperAction.moveBlockDown
    | ContentWrapperAction.moveBlockUp
    | ContentWrapperAction.connectBlocks
    | undefined
): Position;
export function getPosition(
  index: number,
  parentIndex: number | undefined,
  elementType: string | undefined,
  isNestedInCollapsibleOrLayoutGroup: boolean | undefined,
  actionType: string | undefined
): Position | null {
  // Cases that should not happen
  if (
    (elementType === BlockType.layoutgroup && isNestedInCollapsibleOrLayoutGroup) ||
    (actionType === Action.connectBlocks &&
      elementType !== BlockType.contentimage &&
      elementType !== BlockType.layoutgroup &&
      elementType !== BlockType.pdffiledetails &&
      elementType !== BlockType.externalcontent)
  ) {
    return null;
  }
  // LayoutGroup needs two positions.
  if (elementType === BlockType.layoutgroup) {
    return {
      positionImage: getIndex(0, index, parentIndex),
      positionText: getIndex(1, index, parentIndex),
    };
  }

  // ConnectBlock action of Image Block must know its position and position of following block
  if (
    actionType === Action.connectBlocks &&
    (elementType === BlockType.contentimage ||
      elementType === BlockType.externalcontent ||
      elementType === BlockType.pdffiledetails)
  ) {
    return {
      positionImage: getIndex(index, parentIndex),
      positionText: getIndex(index + 1, parentIndex),
    };
  }

  return {
    position: getIndex(index, parentIndex),
  };
}

/**
 * Disabling ActionKeys.
 * Moving is not possible outside of array (highest, lowest position).
 * Same with delete.
 */
export function isActionDisabled(
  actionType: string,
  index: number,
  numBlocks: number,
  elementType?: string,
  isNestedInCollapsibleOrLayoutGroup?: boolean,
  collapsibleParent?: boolean
) {
  switch (actionType) {
    case Action.moveBlockUp:
      if (collapsibleParent) {
        return false;
      }

      if (elementType === BlockType.layoutgroup) {
        return index === 0;
      }

      return isNestedInCollapsibleOrLayoutGroup || index === 0;
    case Action.moveBlockDown:
      if (collapsibleParent) {
        return false;
      }

      if (elementType === BlockType.layoutgroup) {
        return index === numBlocks - 1;
      }

      return isNestedInCollapsibleOrLayoutGroup || index === numBlocks - 1;
    case Action.connectBlocks:
      return false;
    case Action.deleteBlock:
      return !!isNestedInCollapsibleOrLayoutGroup;
    default:
      return false;
  }
}

export const actionKeyToIcon = {
  [Action.connectBlocks]: Icons.CONNECT_BLOCKS,
  [Action.deleteBlock]: Icons.DELETE_TRASHCAN,
  [Action.moveBlockDown]: Icons.MOVE_DOWN,
  [Action.moveBlockUp]: Icons.MOVE_UP,
};

export function nextBlockIsRichtext(blocks: ContentWrapperBlocks[], index: number): boolean {
  return blocks[index + 1]?.elementType !== BlockType.richtext;
}

export function belowIsCollapsibleContainer(blocks: ContentWrapperBlocks[], index: number): number | undefined {
  if (
    blocks.at(index)?.elementType !== BlockType.collapsiblecontainer &&
    blocks.at(index + 1)?.elementType === BlockType.collapsiblecontainer
  ) {
    return 0; // the index of the first element in collapsible container (it's always 0, even if there are none)
  }

  return undefined;
}
export function aboveIsCollapsibleContainer(
  blocks: ContentWrapperBlocks[],
  index: number,
  collapsibleBlocksNumberOfBlocks: Map<string, number>
): number | undefined {
  if (
    blocks.at(index)?.elementType !== BlockType.collapsiblecontainer &&
    blocks.at(index - 1)?.elementType === BlockType.collapsiblecontainer
  ) {
    // "as any" because of block typing too complex
    const collapsibleContainer = blocks.at(index - 1) as any;

    if (collapsibleContainer) {
      // when there is no entry in the map for the collapsible container return 0 as it has no children then
      return collapsibleBlocksNumberOfBlocks.get(collapsibleContainer.uuid) || 0;
    }
  }

  return undefined;
}

/** Focus a block's headline based on the block's id attribute.
 * Extensively used in ContentWrapper to focus the headline but visually highlight the block instead.
 * Focus on headline keeps screen reader output reduced.
 * We set tabindex -1 to the headline, so we can focus it. We remove the tabindex later on blur.
 * At the same time, we set a class on the blockInteractions element to visually highlight the current position.
 * The blockInteractions element is scrolled smoothly into view to preserve cursor position, see SON-12585
 */
export const focusBlockHeadlineById = (id: string) => {
  const focusElement = document.getElementById(id);
  const focusElementHeadline = focusElement?.getElementsByClassName('block-interactions__headline')?.item(0);

  if (focusElement && focusElementHeadline) {
    focusNonFocusableElementAndToggleClassName(
      focusElementHeadline as HTMLElement,
      focusElement,
      'block-interactions--focus-on-headline'
    );

    focusElement.scrollIntoView({ behavior: 'smooth' });
  }
};
